import { PageHero, Container, CtaBand } from "@/components/site/blocks";
import { Reveal } from "@/components/site/motion";
import { useSeo } from "@/components/site/useSeo";
import { DOC_URL } from "@/components/site/nav";
import { Play } from "lucide-react";

const ROLES = ["Author", "Inspirational Speaker", "Filmmaker", "Inventor"];
const VALUES = ["Faith", "Resilience", "Independence", "Accessibility", "Purpose", "Personal growth"];

export default function About() {
  useSeo("About John E. Mason", "Meet John E. Mason — Author, Inspirational Speaker, Filmmaker, and Inventor — and his story of faith, resilience, and independence.");
  return (
    <>
      <PageHero testid="about-hero" label="About John" lines={["John E.", <span key="m" className="italic text-[#F3C649]">Mason.</span>]}
        intro="Author, Inspirational Speaker, Filmmaker, and Inventor." />
      <section className="pb-24 sm:pb-32" data-testid="about-story">
        <Container className="grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <figure className="relative lg:sticky lg:top-28" data-testid="about-portrait">
              <div aria-hidden="true" className="absolute -inset-4 -z-10 rounded-t-full border border-[#D4AF37]/25" />
              <div className="aspect-[4/5] overflow-hidden rounded-t-full rounded-b-3xl border border-[#D4AF37]/40 shadow-[0_40px_120px_-40px_rgba(243,198,73,0.4)]">
                <img src="/images/john-mason.jpg" alt="John E. Mason smiling outdoors in a white cap and white T-shirt" className="h-full w-full object-cover object-[50%_30%]" />
              </div>
              <figcaption className="mt-4 text-sm uppercase tracking-[0.2em] text-[#C8C5BB]">John E. Mason</figcaption>
            </figure>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <ul className="flex flex-wrap gap-3" aria-label="John's roles">
                {ROLES.map((r) => <li key={r} className="rounded-full bg-[#F3C649] px-4 py-2 text-sm font-semibold text-[#0A0B0E]">{r}</li>)}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-12 font-display text-3xl leading-[1.2] sm:text-4xl">
                John lives with a spinal cord injury. He has built a life of purpose around it — and he shares that journey openly.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8 space-y-5 text-lg leading-relaxed text-[#C8C5BB]">
              <p>His story is about faith, resilience, and independence. It is also about access — to places, to opportunity, and to the support that helps people move forward.</p>
              <div className="!mt-10 grid items-center gap-6 rounded-3xl border border-[#D4AF37]/25 bg-[#12141C] p-5 sm:grid-cols-[180px_1fr] sm:p-6" data-testid="about-documentary-card">
                <img src="/images/documentary-poster.jpg" loading="lazy" className="mx-auto aspect-[2/3] w-44 rounded-xl border border-[#D4AF37]/40 object-cover shadow-[0_30px_60px_-30px_rgba(243,198,73,0.6)] sm:w-full"
                  alt="Poster for The John Mason Story and His Journey with God, showing John's face in a hood blended with an ocean sunset" data-testid="about-documentary-poster" />
                <div>
                  <p>Through his documentary, <span className="italic text-[#F9F8F6]">The John Mason Story and His Journey with God</span>, John invites audiences into his experience. Then he turns the conversation toward them: their goals, their voice, and their next step.</p>
                  <a href={DOC_URL} target="_blank" rel="noopener noreferrer" className="btn-gold mt-6" data-testid="about-documentary-link">
                    <Play size={16} aria-hidden="true" fill="currentColor" /> Watch on Vimeo<span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </div>
              </div>
              <p className="!mt-8">JEM is how he brings that conversation to schools, colleges, agencies, community groups, and employers.</p>
            </Reveal>
            <Reveal delay={0.2}>
              <h2 className="label mt-14">What John's story centers on</h2>
              <ul className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/25 sm:grid-cols-3">
                {VALUES.map((v) => <li key={v} className="bg-[#12141C] p-5 font-display text-2xl">{v}</li>)}
              </ul>
            </Reveal>
          </div>
        </Container>
      </section>
      <CtaBand title="Invite John to speak." text="Request a screening, keynote, or workshop for your school, agency, campus, or workplace." />
    </>
  );
}
